import React, {useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    useDisclosure,
    Link as NavLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useProducts} from '@salesforce/commerce-sdk-react'
import Title from '../shop-the-look/partials/title'
import Description from '../shop-the-look/partials/description'

import CloudinaryImage from '../../../cloudinary-media/image'
import ShopThisLookModal from '../shop-the-look/partials/modal'
import {useGlobaleContext} from '../../../../contexts/globale'
import {SELECTED_ATTRIBUTES_WIDGET_PRODUCTS} from '../../../../constants'
import {Link as RouteLink} from 'react-router-dom'

export const ShopTheLookSliderItem = ({
    title,
    titleTag,
    titleColor,
    titleSizeLargeDesktop,
    titleSizeMobile,
    titleSizeSmallDesktop,
    titleSizeTablet,
    image,
    imageAlt,
    titleFontFamily,
    titleAlign,
    description,
    descriptionTag,
    descriptionFontFamily,
    descriptionColor = 'black',
    descriptionAlign,
    btnText,
    btnFontFamily,
    btnAlign,
    btnColor,
    btnBgColor,
    ctaType = 'button',
    redirectionUrl = '',
    noOfSlides,
    showContentBelowImage = false,
    ...rest
}) => {
    const selectedAttributes = SELECTED_ATTRIBUTES_WIDGET_PRODUCTS
    const modalDisclosure = useDisclosure()
    const [loadProducts, setLoadProducts] = useState(false)
    const {countryISO, currencyCode} = useGlobaleContext()
    const textRef = useRef(null)

    const productIds = []
    let index = 1
    const uniqueProductIds = new Set()
    while (`product${index}` in rest) {
        uniqueProductIds.add(rest[`product${index}`])
        delete rest[`product${index}`]
        index++
    }
    productIds.push(...uniqueProductIds)

    const {data: products} = useProducts(
        {
            parameters: {
                ids: productIds.join(','),
                expand: ['availability', 'links', 'promotions', 'images', 'prices', 'variations'],
                perPricebook: true,
                select: selectedAttributes,
                allVariationProperties: true,
                allImages: true,
                c_selectedCountry: countryISO,
                c_selectedCurrency: currencyCode
            }
        },
        {
            enabled: Boolean(productIds.length) && loadProducts,
            keepPreviousData: true
        }
    )

    const handleClick = () => {
        if (!redirectionUrl) {
            setLoadProducts(true)
            modalDisclosure.onOpen()
        }
    }

    const hexToRgba = (hex, alpha = 1) => {
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
        hex = hex?.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b)

        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
        return result
            ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
                  result[3],
                  16
              )}, ${alpha})`
            : hex
    }

    let redirectionProps = {}
    if (redirectionUrl) {
        const isExternalDomain = redirectionUrl?.indexOf('.com') !== -1
        redirectionProps = isExternalDomain
            ? {as: NavLink, href: redirectionUrl}
            : {as: RouteLink, to: redirectionUrl}
    }
    const analyticsProps = {
        component_id: rest.variant || rest.typeId,
        component_name: title || rest.name || 'ShopTheLookSliderItem'
    }
    const isWhiteBg =
        btnBgColor?.trim().toLowerCase() === 'white' ||
        btnBgColor?.trim().toLowerCase() === '#fff' ||
        btnBgColor?.trim().toLowerCase() === '#ffffff'
    const hoverBgValue = isWhiteBg
        ? 'color-mix(in srgb, white 90%, black 10%)'
        : `color-mix(in srgb, ${btnBgColor} 88%, white 12%)`
    return (
        <Box className="stl-wrapper">
            <Button
                ref={textRef}
                className="stl-button"
                onClick={handleClick}
                variant="unstyled"
                position="relative"
                width="100%"
                height="auto"
                maxWidth="auto"
                maxHeight="auto"
                aspectRatio="344:416"
                whiteSpace="normal"
                {...redirectionProps}
            >
                <CloudinaryImage
                    src={image}
                    alt={imageAlt || title || ''}
                    aspectRatio="344:416"
                    imgSizes={{
                        base: '100vw',
                        md: '37vw',
                        lg: '37vw'
                    }}
                    sizes={[375, 390, 414, 640]}
                    styles={{
                        aspectRatio: '344 / 416',
                        width: '100%',
                        height: 'auto',
                        htmlWidth: 344,
                        htmlHeight: 416
                    }}
                />

                <Box
                    position="absolute"
                    left={{base: '12px', sm: '22px', lg: '20px'}}
                    bottom={{
                        base: '17px',
                        sm: '20px',
                        md: '19px',
                        lg: '30px',
                        'xl-large': '30px'
                    }}
                    width="calc(100% - 40px)"
                >
                    <Box className="stl-title-above">
                        {title && !showContentBelowImage && (
                            <Title
                                title={title}
                                titleColor={titleColor}
                                titleSizeLargeDesktop={titleSizeLargeDesktop}
                                titleSizeMobile={titleSizeMobile}
                                titleFontFamily={titleFontFamily}
                                titleSizeSmallDesktop={titleSizeSmallDesktop}
                                titleSizeTablet={titleSizeTablet}
                                titleTag={titleTag}
                                titleAlign={titleAlign}
                            />
                        )}
                        {description && !showContentBelowImage && (
                            <Description
                                description={description}
                                descriptionTag={descriptionTag}
                                descriptionColor={descriptionColor}
                                noOfSlides={noOfSlides}
                                descriptionFontFamily={descriptionFontFamily}
                                descriptionAlign={descriptionAlign}
                                marginBottom="20px"
                            />
                        )}
                    </Box>
                    {btnText && (
                        <Box
                            width="100%"
                            display="flex"
                            justifyContent={btnAlign == 'center' ? 'center' : ''}
                        >
                            {ctaType === 'button' ? (
                                <Box
                                    opacity="1"
                                    transition="all 0.15s ease-in-out"
                                    background={hexToRgba(btnBgColor, 0.5)}
                                    backgroundColor={hexToRgba(btnBgColor, 0.5)}
                                    color={btnColor}
                                    fontFamily={btnFontFamily}
                                    fontWeight="400"
                                    lineHeight={{base: '17px', lg: '23px'}}
                                    letterSpacing={{base: '0.056px', lg: '0.075px'}}
                                    fontSize={{base: '11px', lg: '15px'}}
                                    borderRadius="22px"
                                    paddingX={{base: '25px'}}
                                    paddingY={{base: '4px'}}
                                    sx={{
                                        _hover: {
                                            backgroundColor: hoverBgValue,
                                            textDecoration: 'none'
                                        }
                                    }}
                                >
                                    {btnText}
                                </Box>
                            ) : (
                                <Box
                                    textDecoration="underline"
                                    textUnderlineOffset="4px"
                                    color={btnColor}
                                    fontFamily={btnFontFamily}
                                >
                                    {btnText}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                <ShopThisLookModal
                    desktopImage={image}
                    mobileImage={image}
                    products={products}
                    modalDisclosure={modalDisclosure}
                    analyticsProps={analyticsProps}
                />
            </Button>
            {showContentBelowImage && (
                <Box className="stl-title-description" margin="15px 10px 0px 10px">
                    <Box className="stl-title-below">
                        {title && (
                            <Title
                                title={title}
                                titleColor={titleColor}
                                titleSizeLargeDesktop={titleSizeLargeDesktop}
                                titleSizeMobile={titleSizeMobile}
                                titleFontFamily={titleFontFamily}
                                titleSizeSmallDesktop={titleSizeSmallDesktop}
                                titleSizeTablet={titleSizeTablet}
                                titleTag={titleTag}
                                titleAlign={titleAlign}
                            />
                        )}
                    </Box>
                    <Box marginTop="10px" className="stl-description">
                        {description && (
                            <Description
                                description={description}
                                descriptionTag={descriptionTag}
                                descriptionColor={descriptionColor}
                                noOfSlides={noOfSlides}
                                descriptionFontFamily={descriptionFontFamily}
                                descriptionAlign={descriptionAlign}
                                marginBottom="20px"
                            />
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    )
}

ShopTheLookSliderItem.propTypes = {
    title: PropTypes.string,
    titleTag: PropTypes.string,
    titleFontFamily: PropTypes.string,
    titleColor: PropTypes.string,
    titleSizeLargeDesktop: PropTypes.string,
    titleSizeMobile: PropTypes.string,
    titleSizeSmallDesktop: PropTypes.string,
    titleSizeTablet: PropTypes.string,
    titleAlign: PropTypes.string,
    description: PropTypes.string,
    descriptionTag: PropTypes.string,
    descriptionFontFamily: PropTypes.string,
    descriptionColor: PropTypes.string,
    descriptionAlign: PropTypes.string,
    btnText: PropTypes.string,
    btnFontFamily: PropTypes.string,
    btnAlign: PropTypes.string,
    btnColor: PropTypes.string,
    btnBgColor: PropTypes.string,
    image: PropTypes.string,
    imageAlt: PropTypes.string,
    showContentBelowImage: PropTypes.bool,
    redirectionUrl: PropTypes.string,
    noOfSlides: PropTypes.any,
    ctaType: PropTypes.string
}

export default ShopTheLookSliderItem
